import { useAutoAnimate } from "@formkit/auto-animate/react";
import debounce from "lodash.debounce";
import { useCallback, useMemo, useState } from "react";

import type { ComparitorFilterTypeEnum, Option } from "@sorcery/db";
import { ADVANCED_FILTER_TYPES, FilterTypeEnum } from "@sorcery/db";
import { AsyncCombobox, Button, Line, ModalBody, ModalHeader, P } from "@sorcery/ui";

import type { AdvancedSearchPayload } from "~/providers/ModalProvider";
import { useModal } from "~/providers/ModalProvider";
import { api } from "~/utils/trpc";

export const AdvancedDeckSearchModal = ({
  filters,
  onConfirm,
}: {
  filters: Option[];
  onConfirm: (payload: AdvancedSearchPayload) => void;
}) => {
  const { closeModal } = useModal();
  const [incQuery, setIncQuery] = useState("");
  const [excQuery, setExcQuery] = useState("");

  const [incParent] = useAutoAnimate({ duration: 150 });
  const [excParent] = useAutoAnimate({ duration: 150 });

  const [inclusions, setInclusions] = useState<Option[]>(
    filters.filter((f) => f.type === FilterTypeEnum.INCLUSION),
  );
  const [exclusions, setExclusions] = useState<Option[]>(
    filters.filter((f) => f.type === FilterTypeEnum.EXCLUSION),
  );

  const {
    data: includeResults,
    isFetching: includesFetching,
    isRefetching: includesRefetching,
  } = api.search.cards.useQuery(
    {
      query: incQuery,
    },
    {
      enabled: !!incQuery,
      select: (data) =>
        data.map((o) => ({
          ...o,
          type: FilterTypeEnum.INCLUSION,
        })) satisfies Option[],
    },
  );

  const {
    data: excludeResults,
    isFetching: excludesFetching,
    isRefetching: excludesRefetching,
  } = api.search.cards.useQuery(
    {
      query: excQuery,
    },
    {
      enabled: !!excQuery,
      select: (data) =>
        data.map((o) => ({
          ...o,
          type: FilterTypeEnum.EXCLUSION,
        })) satisfies Option[],
    },
  );

  const incQueryChangeHandler = useCallback((q: string) => {
    setIncQuery(q);
  }, []);

  const debouncedIncChangeHandler = useMemo(
    () => debounce(incQueryChangeHandler, 500),
    [incQueryChangeHandler],
  );

  const excQueryChangeHandler = useCallback((q: string) => {
    setExcQuery(q);
  }, []);

  const debouncedExcChangeHandler = useMemo(
    () => debounce(excQueryChangeHandler, 500),
    [excQueryChangeHandler],
  );

  const onInclude = (o: Option) => {
    // if its not already in the list
    if (!inclusions.find((i) => i.value === o.value)) {
      setInclusions([...inclusions, o]);
    }
  };

  const onExclude = (o: Option) => {
    // if its not already in the list
    if (!exclusions.find((i) => i.value === o.value)) {
      setExclusions([...exclusions, o]);
    }
  };

  const onSearch = () => {
    const payload: AdvancedSearchPayload = {
      newFilters: [...inclusions, ...exclusions],
    };

    // merge with existing filters
    payload.newFilters = [
      ...filters.filter(
        (f) => !ADVANCED_FILTER_TYPES.includes(f.type as FilterTypeEnum | ComparitorFilterTypeEnum),
      ),
      ...payload.newFilters,
    ];

    onConfirm(payload);
    closeModal();
  };

  return (
    <div className="p-6">
      <ModalHeader title="Advanced Search" />

      <ModalBody>
        <P>
          This will scan the contents of a deck's mainboard. It won't check against the sideboard or
          maybeboard.
        </P>

        {/* inclusions */}
        <div className="mb-2 flex w-full flex-row items-center">
          <div className="w-20">Inclusions</div>
          <div className="grow">
            <AsyncCombobox
              placeholder="Enter cards that must be included..."
              loading={includesFetching && !includesRefetching}
              onSelect={onInclude}
              query={incQuery}
              setQuery={debouncedIncChangeHandler}
              options={includeResults ?? []}
              clearAfterSelect={true}
            />
          </div>
        </div>
        {!!inclusions.length && (
          <div className="mb-2 flex w-full flex-row items-center transition duration-700 animate-in fade-in">
            <div className="w-20" />
            <div className="grow" ref={incParent}>
              {inclusions.map((inc) => (
                <div key={inc.label} className="italic">
                  <div className="">{inc.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* exclusions */}
        <div className="mb-2 flex w-full flex-row items-center">
          <div className="w-20">Exclusions</div>
          <div className="grow">
            <AsyncCombobox
              placeholder="Enter cards that cannot be included..."
              loading={excludesFetching && !excludesRefetching}
              onSelect={onExclude}
              query={excQuery}
              setQuery={debouncedExcChangeHandler}
              options={excludeResults ?? []}
            />
          </div>
        </div>
        {!!exclusions.length && (
          <div className="mb-2 flex w-full flex-row items-center transition duration-700 animate-in fade-in">
            <div className="w-20" />
            <div className="grow" ref={excParent}>
              {exclusions.map((exc) => (
                <div key={exc.label} className="italic">
                  <div className="">{exc.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button onClick={onSearch}>Search</Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
